/**
 * Print summaries: reports saved from a page's Print Summary
 * (`PageExportDialog`), each remembering the page it came from (`origin`,
 * `app.reports.origin_*`). The Reports module files them under "My print
 * summaries", apart from the reader's other reports, and the page's Print
 * Summary button counts its own and opens on them, so a reader finds the one
 * they saved rather than making another.
 *
 * A page's own is the same area and entity: a pricing point's summaries from
 * any of its views, an RFQ's from its Results tab whatever the offering. What
 * it showed of it (`detail`: the view, the offering) tells them apart.
 */
import type { REPORT_ORIGIN_AREAS } from "@/types/generated/report-builder-constants";
import type { CustomReportSummary, ReportOrigin } from "@/types/reports";

/** A page a Print Summary saves from: the server's allowlist as a union. */
export type ReportOriginArea = (typeof REPORT_ORIGIN_AREAS)[number];

/** Each area's name, as the Reports module says where a summary came from. */
export const ORIGIN_AREA_LABELS: Record<ReportOriginArea, string> = {
  mi_hub: "Market Intelligence",
  mi_region: "Market Intelligence",
  rfq_results: "RFQ results",
  rfq_insights: "RFQ insights",
  deal_proposal: "Deal proposal",
};

export function originAreaLabel(area: string): string {
  return (ORIGIN_AREA_LABELS as Record<string, string>)[area] ?? "Print Summary";
}

/** Whether a saved report is a print summary: one with a page it came from. */
export function isPrintSummary(report: Pick<CustomReportSummary, "origin">): boolean {
  return report.origin != null;
}

/** Whether a saved report is a print summary of this page's entity. */
export function isPrintSummaryOf(report: Pick<CustomReportSummary, "origin">, origin: ReportOrigin): boolean {
  return report.origin?.area === origin.area && report.origin.entity === origin.entity;
}

/** A Market Intelligence pricing point's hub workspace, on one of its views (Strips, Curve…). */
export function miHubOrigin(hub: { code: string; name: string }, view: string | null, path: string): ReportOrigin {
  return { area: "mi_hub", entity: hub.code, detail: view, label: hub.name, path };
}

/** A Market Intelligence region's Weekly Briefing. */
export function miRegionOrigin(iso: string, path: string): ReportOrigin {
  return { area: "mi_region", entity: iso, detail: null, label: `${iso} Weekly Briefing`, path };
}

/** An Energy Advisor RFQ's Results or Insights tab, for one offering on Results. */
export function rfqOrigin(
  area: Extract<ReportOriginArea, "rfq_results" | "rfq_insights">,
  rfq: { id: string; customer_id: string; rfq_number: number; deal_name: string },
  clientName: string | null,
  detail: string | null,
): ReportOrigin {
  return {
    area,
    entity: rfq.id,
    detail,
    label: `${clientName ?? rfq.deal_name} · RFQ ${rfq.rfq_number}`,
    path: `/energy-advisor/${rfq.customer_id}/rfqs/${rfq.id}`,
  };
}

/** A deal's Proposal tab, for one offering. */
export function dealProposalOrigin(
  deal: { id: string; name: string },
  detail: string | null,
  path: string,
): ReportOrigin {
  return { area: "deal_proposal", entity: deal.id, detail, label: deal.name, path };
}
